import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useFeatures } from '@/lib/useFeatures';
import { notifyXp } from '@/lib/xpToast';
import { CheckCircle2, ArrowLeft, ArrowRight, Lock, Trophy, BookOpen, Sparkles } from 'lucide-react';
import LessonAudioPlayer from '@/components/LessonAudioPlayer';
import CinematicReader from '@/components/CinematicReader';

export default function LessonReader() {
  const { lessonId } = useParams();
  const { user, refresh } = useAuth();
  const { isOn } = useFeatures();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [reflection, setReflection] = useState('');
  const [exerciseData, setExerciseData] = useState({});
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [tab, setTab] = useState('read');
  const [cinematic, setCinematic] = useState(false);
  const topRef = useRef(null);

  const load = async () => {
    setError('');
    setQuizResult(null);
    setReflection('');
    setExerciseData({});
    setQuizAnswers([]);
    try {
      // Step 1: fetch lesson first (returns course_slug)
      const lessonR = await api.get(`/lessons/${lessonId}`);
      const courseSlug = lessonR.data.course_slug || 'headspace';
      const [courseR, progR] = await Promise.all([
        api.get(`/courses/${courseSlug}`),
        user ? api.get('/progress').then(r => r.data.completed_lessons || []) : Promise.resolve([]),
      ]);
      setLesson(lessonR.data);
      setAllLessons(courseR.data.lessons || []);
      setCompleted((progR || []).includes(lessonId));
      setQuizAnswers(new Array((lessonR.data.quiz || []).length).fill(-1));
      setTab('read');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) { nav('/login', { state: { from: `/learn/${lessonId}` } }); return; }
      if (code === 402) { nav('/unlock'); return; }
      setError(e?.response?.data?.detail || 'Failed to load lesson');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lessonId]);

  if (error) return <div className="kc-container py-16 text-[color:var(--kc-danger)]" data-testid="lesson-error">{error}</div>;
  if (!lesson) return <div className="kc-container py-16 text-muted-kc">Loading lesson…</div>;

  const idx = allLessons.findIndex(l => l.id === lesson.id);
  const prev = idx > 0 ? allLessons[idx - 1] : null;
  const next = idx < allLessons.length - 1 ? allLessons[idx + 1] : null;

  const saveReflection = async () => {
    if (!reflection.trim()) return;
    // Privacy-first: we do not store reflection content. Encourage user to write in their own notebook/app.
    alert("Great reflection! For your privacy, we don't save the content — copy it to your own journal (paper, Notes app, etc.). Visit the Resources page to log today's journal streak.");
    setReflection('');
  };

  const saveExercise = async () => {
    if (!user) { nav('/login'); return; }
    if (Object.keys(exerciseData).length === 0) return;
    await api.post('/exercise', { lesson_id: lesson.id, data: exerciseData });
    alert('Exercise saved.');
  };

  const submitQuiz = async () => {
    if (!user) { nav('/login'); return; }
    const { data } = await api.post('/quiz/submit', { lesson_id: lesson.id, answers: quizAnswers });
    setQuizResult(data);
  };

  const markComplete = async () => {
    if (!user) { nav('/login'); return; }
    const r = await api.post('/progress/complete', { lesson_id: lesson.id });
    setCompleted(true);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 2500);
    if (r?.data?.xp?.xp_gained) {
      notifyXp(r.data.xp, `Lesson complete \u00B7 ${lesson.title}`);
      window.dispatchEvent(new Event('kc:xp-updated'));
    }
    refresh();
  };

  return (
    <div className="bg-white" ref={topRef}>
      {cinematic && isOn('cinematic_read') && (
        <CinematicReader lesson={lesson} onClose={() => setCinematic(false)} />
      )}

      {showCelebration && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-ink text-white px-5 py-3 rounded-[var(--kc-radius-md)] shadow-kc inline-flex items-center gap-2 kc-fade-up" data-testid="lesson-celebration">
          <Trophy className="text-gold"/> Lesson complete. Keep going.
        </div>
      )}

      {/* Top bar */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-kc">
        <div className="kc-container py-3 flex items-center gap-3">
          <Link to="/courses/headspace" className="text-sm text-muted-kc hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="lesson-back"><ArrowLeft size={14}/> Curriculum</Link>
          <div className="flex-1 text-xs text-muted-kc truncate">{lesson.subtitle} — <span className="text-ink font-semibold">{lesson.title}</span></div>
          {!completed ? (
            <button onClick={markComplete} className="h-9 px-3 rounded-[var(--kc-radius-sm)] bg-ink text-white text-xs font-semibold hover:bg-black/90 transition-colors" data-testid="lesson-mark-complete">Mark complete</button>
          ) : (
            <span className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-gold-soft border border-gold text-ink text-xs font-semibold" data-testid="lesson-completed-badge"><CheckCircle2 size={14}/> Completed</span>
          )}
        </div>
        {/* Tabs */}
        <div className="kc-container pb-2 flex gap-1 overflow-x-auto">
          {['read', 'takeaways', 'reflect', 'exercise', 'quiz'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full whitespace-nowrap ${tab === t ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2'} transition-colors`} data-testid={`tab-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="reader-container py-8 md:py-12">
        {tab === 'read' && (
          <article>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">{lesson.subtitle}</div>
            <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] mt-2" data-testid="lesson-title">{lesson.title}</h1>
            <div className="mt-4 inline-flex items-center gap-1 text-xs text-muted-kc"><BookOpen size={12}/> {lesson.reading_time_min} min read</div>
            {isOn('cinematic_read') && (
              <button
                type="button"
                onClick={() => setCinematic(true)}
                className="mt-5 group h-12 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold border border-[color:var(--kc-gold)]/40 hover:bg-black hover:border-[color:var(--kc-gold)] hover:shadow-[0_0_24px_rgba(212,164,55,0.25)] transition-all"
                data-testid="lesson-cinematic-launch"
              >
                <Sparkles size={14} className="text-[color:var(--kc-gold)] group-hover:scale-110 transition-transform"/>
                <span className="tracking-wide uppercase text-xs">Enter Cinematic Mode</span>
              </button>
            )}
            <div className="mt-6">
              <LessonAudioPlayer
                text={`${lesson.title}. ${lesson.subtitle || ''}. ${lesson.body || ''}`}
                title={`Listen — ${lesson.title}`}
                audioUrl={lesson.audio_url}
              />
            </div>
            <div className="prose-reader mt-8" data-testid="lesson-body">
              {(lesson.body || '').split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </article>
        )}

        {tab === 'takeaways' && (
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Key Takeaways</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">What to remember</h2>
            <div className="mt-6">
              <LessonAudioPlayer
                text={`Key takeaways for ${lesson.title}. ${(lesson.key_takeaways || []).map((t, i) => `${i+1}. ${t}`).join('. ')}`}
                title="Listen — Key Takeaways"
              />
            </div>
            <ul className="mt-6 space-y-3" data-testid="lesson-takeaways">
              {(lesson.key_takeaways || []).map((t, i) => (
                <li key={i} className="bg-surface-2 border border-kc rounded-[var(--kc-radius-md)] p-4 flex gap-3">
                  <span className="font-display text-2xl text-gold w-8 flex-shrink-0">{String(i+1).padStart(2,'0')}</span>
                  <span className="text-ink-2 text-base leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === 'reflect' && (
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Reflection</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">Write it out</h2>
            <p className="mt-3 font-reader text-lg text-ink-2 italic border-l-2 border-gold pl-4 bg-surface-2 py-3 rounded-r-[var(--kc-radius-sm)]" data-testid="lesson-reflection-prompt">{lesson.reflection_prompt}</p>
            <div className="mt-3 text-xs text-muted-kc bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-sm)] p-3">
              <strong>Privacy note:</strong> For your security, your reflection is NOT saved on this site. Write here as a scratchpad, then copy it to your own paper journal or notes app. <Link to="/resources" className="font-semibold underline">Log today's journal habit →</Link>
            </div>
            <textarea value={reflection} onChange={(e) => setReflection(e.target.value)} placeholder="Take your time. Then copy this to your own private journal." rows={10} className="mt-4 w-full p-4 rounded-[var(--kc-radius-md)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] font-reader text-base leading-relaxed" data-testid="reflection-textarea" />
            <button onClick={saveReflection} className="mt-3 h-11 px-5 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="reflection-save">Done reflecting</button>
          </div>
        )}

        {tab === 'exercise' && lesson.exercise && (
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Exercise</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">{lesson.exercise.title}</h2>
            {lesson.exercise.instructions && (
              <p className="mt-3 text-ink-2 whitespace-pre-wrap">{lesson.exercise.instructions}</p>
            )}
            <div className="mt-5 space-y-4" data-testid="lesson-exercise">
              {(lesson.exercise.fields || []).map((f, idx) => {
                const key = typeof f === 'string' ? f : f.key;
                const label = typeof f === 'string' ? f.replace(/_/g, ' ') : (f.label || key);
                return (
                  <div key={key || idx}>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-kc mb-1">{label}</label>
                    <textarea rows={3} value={exerciseData[key] || ''} onChange={(e) => setExerciseData({ ...exerciseData, [key]: e.target.value })} className="w-full p-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] text-base" data-testid={`exercise-${key}`} />
                  </div>
                );
              })}
              {Array.isArray(lesson.exercise.checkboxes) && lesson.exercise.checkboxes.length > 0 && (
                <div className="pt-1">
                  <div className="text-[11px] uppercase tracking-wider text-muted-kc mb-2">Check all that apply</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {lesson.exercise.checkboxes.map((cb, i) => {
                      const key = typeof cb === 'string' ? `cb_${i}` : cb.key;
                      const label = typeof cb === 'string' ? cb : cb.label;
                      const checked = !!exerciseData[key];
                      return (
                        <label key={key} className={`flex items-start gap-2 p-3 rounded-[var(--kc-radius-sm)] border cursor-pointer transition-colors ${checked ? 'border-gold bg-gold-soft' : 'border-kc hover:bg-surface-2'}`} data-testid={`exercise-cb-${key}`}>
                          <input type="checkbox" checked={checked} onChange={(e) => setExerciseData({ ...exerciseData, [key]: e.target.checked })} className="mt-1 accent-[color:var(--kc-gold)]" />
                          <span className="text-sm text-ink">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {Array.isArray(lesson.exercise.checkboxes_have) && lesson.exercise.checkboxes_have.length > 0 && (
                <div className="pt-1">
                  <div className="text-[11px] uppercase tracking-wider text-muted-kc mb-2">What you could have instead</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {lesson.exercise.checkboxes_have.map((cb, i) => {
                      const key = typeof cb === 'string' ? `cbh_${i}` : cb.key;
                      const label = typeof cb === 'string' ? cb : cb.label;
                      const checked = !!exerciseData[key];
                      return (
                        <label key={key} className={`flex items-start gap-2 p-3 rounded-[var(--kc-radius-sm)] border cursor-pointer transition-colors ${checked ? 'border-gold bg-gold-soft' : 'border-kc hover:bg-surface-2'}`} data-testid={`exercise-cbh-${key}`}>
                          <input type="checkbox" checked={checked} onChange={(e) => setExerciseData({ ...exerciseData, [key]: e.target.checked })} className="mt-1 accent-[color:var(--kc-gold)]" />
                          <span className="text-sm text-ink">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {(lesson.exercise.fields_after || []).map((f, idx) => {
                const key = typeof f === 'string' ? f : f.key;
                const label = typeof f === 'string' ? f.replace(/_/g, ' ') : (f.label || key);
                return (
                  <div key={`after-${key || idx}`}>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-kc mb-1">{label}</label>
                    <textarea rows={3} value={exerciseData[key] || ''} onChange={(e) => setExerciseData({ ...exerciseData, [key]: e.target.value })} className="w-full p-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)] text-base" data-testid={`exercise-after-${key}`} />
                  </div>
                );
              })}
            </div>
            <button onClick={saveExercise} className="mt-4 h-11 px-5 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="exercise-save">Save exercise</button>
          </div>
        )}

        {tab === 'quiz' && (
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Quick Check</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">Lesson quiz</h2>
            <div className="mt-6 space-y-5" data-testid="lesson-quiz">
              {(lesson.quiz || []).map((q, qi) => (
                <div key={qi} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
                  <div className="font-semibold text-ink">{qi+1}. {q.q}</div>
                  <div className="mt-3 space-y-2">
                    {q.options.map((opt, oi) => {
                      const selected = quizAnswers[qi] === oi;
                      const showCorrect = quizResult && quizResult.correct_answers[qi] === oi;
                      const showWrong = quizResult && selected && !quizResult.correct[qi];
                      const cls = quizResult
                        ? (showCorrect ? 'border-gold bg-gold-soft' : showWrong ? 'border-[color:var(--kc-danger)] bg-red-50' : 'border-kc')
                        : (selected ? 'border-ink bg-surface-2' : 'border-kc hover:bg-surface-2');
                      return (
                        <button key={oi} disabled={!!quizResult} onClick={() => { const a = [...quizAnswers]; a[qi] = oi; setQuizAnswers(a); }} className={`w-full text-left p-3 rounded-[var(--kc-radius-sm)] border ${cls} transition-colors disabled:cursor-default`} data-testid={`quiz-q${qi}-opt${oi}`}>{opt}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {!quizResult ? (
              <button onClick={submitQuiz} disabled={quizAnswers.some(a => a === -1)} className="mt-5 h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-50" data-testid="quiz-submit">Submit quiz</button>
            ) : (
              <div className="mt-5 p-5 rounded-[var(--kc-radius-md)] border border-gold bg-gold-soft" data-testid="quiz-result">
                <div className="font-display uppercase text-2xl">Score: {quizResult.score}/{quizResult.total} {quizResult.passed ? '— nice work.' : '— review and try again.'}</div>
                <button onClick={() => { setQuizResult(null); setQuizAnswers(new Array((lesson.quiz || []).length).fill(-1)); }} className="mt-3 text-sm font-semibold underline" data-testid="quiz-retry">Retry</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="border-t border-kc">
        <div className="kc-container py-6 flex items-center justify-between gap-3">
          {prev ? (
            <Link to={`/learn/${prev.id}`} className="flex items-center gap-2 text-sm font-semibold hover:text-gold transition-colors" data-testid="lesson-prev"><ArrowLeft size={16}/> Previous</Link>
          ) : <span />}
          {!completed && <button onClick={markComplete} className="h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="lesson-bottom-complete">Mark complete <CheckCircle2 size={16} className="inline ml-1"/></button>}
          {next ? (
            <Link to={`/learn/${next.id}`} className="flex items-center gap-2 text-sm font-semibold hover:text-gold transition-colors" data-testid="lesson-next">Next <ArrowRight size={16}/></Link>
          ) : (
            <Link to="/certificate" className="flex items-center gap-2 text-sm font-semibold text-gold" data-testid="lesson-finish">Finish course <ArrowRight size={16}/></Link>
          )}
        </div>
      </div>
    </div>
  );
}
